import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useI18n } from '~Hooks';
import Toggle from '~ComponentsImproved/Toggle';

const Wrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  min-height: 64px;
  border-bottom: 1px solid var(--colorBlueGray1200);

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
    border-bottom: none;
  }

  &:only-of-type {
    border-radius: 8px;
  }
`;

const IndicatorBall = styled(Box)`
  border-radius: 50%;
  width: 12px;
  height: 12px;
`;

const ItalicText = styled.span`
  font-style: italic;
  color: var(--colorBlueGray200);
  font-size: 14px;
`;

const QtyButton = styled(Button)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
`;

interface Props {
  name: string;
  currentCount: number | null;
  isOutOfStock: boolean;
  newCount: number;
  overrideEnabled: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  onToggleOverride: (isActive: boolean) => void;
}

const BatchProductRow: FC<Props> = ({
  name,
  currentCount,
  isOutOfStock,
  newCount,
  overrideEnabled,
  onIncrement,
  onDecrement,
  onToggleOverride,
}) => {
  const { i18n } = useI18n();

  const incrementButton = useButton({ onClick: onIncrement });
  // In non-override mode, allow going negative down to -currentCount
  const minCount = overrideEnabled ? 0 : currentCount != null ? -currentCount : 0;
  const decrementButton = useButton({ onClick: onDecrement, disabled: newCount <= minCount });

  const hasInventory = isOutOfStock || currentCount !== null;

  return (
    <Wrapper>
      {/* Product name + current stock status */}
      <Box display="flex" flexDirection="column" fluid>
        <Heading level={5} color="var(--colorWhite)" bottom={0}>
          {name}
        </Heading>
        <Box display="flex" alignItems="center" gap="xs">
          {hasInventory ? (
            <>
              <IndicatorBall
                backgroundColor={isOutOfStock ? 'colorStatusRed' : 'colorStatusGreen'}
              />
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {isOutOfStock
                  ? i18n('Inventory.OutOfStock')
                  : `${currentCount} ${i18n('Inventory.XInStock')}`}
              </Paragraph>
            </>
          ) : (
            <>
              <IndicatorBall backgroundColor="colorStatusGray" />
              <ItalicText>{i18n('Inventory.StockNotSet')}</ItalicText>
            </>
          )}
        </Box>
      </Box>

      {/* Override toggle */}
      <Toggle
        label={i18n('Inventory.Override.Short')}
        initialValue={overrideEnabled}
        onToggle={onToggleOverride}
        darkBackground
        lightText
      />

      {/* Quantity controls */}
      <Box display="flex" alignItems="center" gap="small">
        <QtyButton {...decrementButton}>−</QtyButton>
        <Paragraph level={4} fontWeight={600} color="var(--colorWhite)">
          {newCount}
        </Paragraph>
        <QtyButton {...incrementButton}>+</QtyButton>
      </Box>
    </Wrapper>
  );
};

export default BatchProductRow;
